import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripVerticalIcon, LayersIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { newBlock, saveLanding, setLandingStatus } from '../../services/landingService';
import { newId } from '../../services/authService';
import { deleteComponent, instantiateComponent, saveComponent } from '../../services/componentService';
import { BLOCK_LIBRARY, blockDefinition } from '../../data/blockLibrary';
import { FONT_OPTIONS } from '../../data/fonts';
import { LandingBlock, LandingPage, LandingTheme } from '../../types';
import { BlockSettings } from '../../components/landings/BlockSettings';
import { StyleSettings } from '../../components/landings/StyleSettings';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { LandingRenderer } from '../../components/public/LandingRenderer';
import { ThankYouView } from '../../components/public/ThankYouView';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
const THEMES: {
  value: LandingTheme;
  label: string;
}[] = [{
  value: 'blanco_premium',
  label: 'Blanco premium'
}, {
  value: 'claro_corporativo',
  label: 'Claro corporativo'
}, {
  value: 'minimalista',
  label: 'Minimalista'
}];
export function LandingEditor() {
  const {
    id = ''
  } = useParams();
  const {
    organization
  } = useAuth();
  const {
    landings,
    forms,
    components
  } = useWorkspace();
  const stored = landings.find(l => l.id === id);
  const [draft, setDraft] = useState<LandingPage | null>(stored ?? null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  /** Solo se arrastra desde el asa, para no mover bloques por accidente. */
  const [handleId, setHandleId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  /** Pestaña activa: con «gracias» la vista previa muestra la página de gracias. */
  const [tab, setTab] = useState('bloques');
  const [saveComponentFor, setSaveComponentFor] = useState<LandingBlock | null>(null);
  const [componentName, setComponentName] = useState('');
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  if (!draft) {
    return <div className="rounded-2xl border border-border bg-background p-10 text-center" data-id="element-928" data-mp="2y0g5bxqlw79q">
        <p className="text-sm text-muted-foreground" data-id="element-929" data-mp="322b876o6qspm">Esta landing no existe.</p>
        <Link to="/app/landings" className="mt-4 inline-block" data-id="element-930" data-mp="370us71j7xufc">
          <Button variant="secondary" data-id="element-931" data-mp="3bz63lgp5wczo">Volver a landings</Button>
        </Link>
      </div>;
  }
  const form = forms.find(f => f.id === draft.formId);
  const blocks = draft.pageConfig.blocks;
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;
  function update(partial: Partial<LandingPage>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  function updateConfig(partial: Partial<LandingPage['pageConfig']>) {
    update({
      pageConfig: {
        ...draft!.pageConfig,
        ...partial
      }
    });
  }
  function updateBlock(block: LandingBlock) {
    updateConfig({
      blocks: blocks.map(b => b.id === block.id ? block : b)
    });
  }
  function addBlock(block: LandingBlock) {
    updateConfig({
      blocks: [...blocks, block]
    });
    setSelectedBlockId(block.id);
    setAddOpen(false);
  }
  function duplicateBlock(block: LandingBlock) {
    const copy: LandingBlock = {
      ...(JSON.parse(JSON.stringify(block)) as LandingBlock),
      // El id debe ser un UUID válido: `landing_blocks.id` es de tipo uuid.
      id: newId('block')
    };
    const index = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateConfig({
      blocks: next
    });
  }
  /**
   * Reordena solo en vertical: se mueve el objeto completo del bloque, así que
   * conserva id, type, config, style, contenido y visibilidad.
   */
  function reorder(targetId: string) {
    setDragOverId(null);
    setHandleId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const next = [...blocks];
    const fromIndex = next.findIndex(b => b.id === dragId);
    const toIndex = next.findIndex(b => b.id === targetId);
    next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
    updateConfig({
      blocks: next
    });
    setDragId(null);
  }
  async function handleSave(status?: LandingPage['status']) {
    setSaving(true);
    setSaveError(null);
    try {
      await saveLanding(draft!);
      if (status) await setLandingStatus(draft!.id, status);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error desconocido.');
    } finally {
      setSaving(false);
    }
  }
  async function confirmSaveComponent() {
    if (!saveComponentFor || !organization) return;
    await saveComponent(organization.id, componentName || blockDefinition(saveComponentFor.type).label, blockDefinition(saveComponentFor.type).label, saveComponentFor);
    setSaveComponentFor(null);
    setComponentName('');
  }
  return <>
      <Link to="/app/landings" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-932" data-mp="2b4pd9zd3tig4">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-933" data-mp="225vatsd3dxun" />
        Volver a landings
      </Link>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-934" data-mp="1x7k07fez3jbd">
        <div data-id="element-935" data-mp="1s7pcfkqhv9uj">
          <h1 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl" data-id="element-936" data-mp="1s87cytk6wa89">
            {draft.name}
          </h1>
          <p className="text-sm text-muted-foreground" data-id="element-937" data-mp="1nfo1q38z5r6m">
            /landing/{draft.slug} ·{' '}
            {draft.status === 'publicada' ? 'Publicada' : draft.status === 'borrador' ? 'Borrador' : 'Despublicada'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-938" data-mp="19hg7i33bp3z5">
          {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-939" data-mp="19gy48mi8tphq">
              Guardando…
            </span> : saved ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-940" data-mp="14j4vpmtgwjea">
              <CheckIcon className="h-4 w-4" data-id="element-941" data-mp="ue6fplq7g3xa" /> Guardado correctamente
            </span> : <span className="self-center text-sm text-muted-foreground" data-id="element-942" data-mp="zc7wu142lt64">
              Cambios sin guardar
            </span>}
          <Button variant="ghost" onClick={() => handleSave('borrador')} loading={saving} data-id="element-943" data-mp="14ddv0egi28n1">
            Guardar borrador
          </Button>
          <Button variant="secondary" leftIcon={<EyeIcon className="h-4 w-4" data-id="element-945" data-mp="bp9hx7vlmtab" />} onClick={() => window.open(`/landing/${draft.slug}`, '_blank')} data-id="element-944" data-mp="bo6rf126usux">
            Vista previa
          </Button>
          <Button variant="secondary" leftIcon={<CopyIcon className="h-4 w-4" data-id="element-947" data-mp="ll33thv7jz4r" />} onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/landing/${draft.slug}`)} data-id="element-946" data-mp="lm4evmc84lmh">
            Copiar enlace
          </Button>
          {draft.status === 'publicada' ? <Button variant="destructive" onClick={() => handleSave('despublicada')} data-id="element-948" data-mp="3p2bi5rqq8guq">
              Despublicar
            </Button> : <Button onClick={() => handleSave('publicada')} loading={saving} data-id="element-949" data-mp="3tzl612ygwkgg">
              Publicar
            </Button>}
        </div>
      </div>

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-950" data-mp="182q66h4jis50">
          <span data-id="element-951" data-mp="1h11ugchxpdua">
            <strong className="font-semibold" data-id="element-952" data-mp="1h1bihujf5axc">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={() => handleSave()} loading={saving} data-id="element-953" data-mp="1v01k9nh6nys1">
            Reintentar
          </Button>
        </div>}

      <div className="grid items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]" data-id="element-954" data-mp="24re9s8fiecuw">
        {/* Panel izquierdo, con scroll propio para no perder la vista previa */}
        <div className="min-w-0 xl:max-h-[860px] xl:overflow-y-auto xl:pr-1" data-id="element-955" data-mp="1zunwfsv39vd6">
          <Tabs defaultValue="bloques" value={tab} onValueChange={setTab} data-id="element-956" data-mp="2eqda93q4f26t">
            <TabsList data-id="element-957" data-mp="29rjx5ceqxd76">
              <TabsTrigger value="bloques" data-id="element-958" data-mp="29sbr6isp32vc">Bloques</TabsTrigger>
              <TabsTrigger value="diseno" data-id="element-959" data-mp="2tc0yp13cwsvh">Diseño</TabsTrigger>
              <TabsTrigger value="gracias" data-id="element-960" data-mp="2tapskumfv917">Gracias</TabsTrigger>
            </TabsList>

            <TabsContent value="bloques" data-id="element-961" data-mp="2xdclyzyrd20i">
              <p className="mb-3 text-xs text-muted-foreground" data-id="element-962" data-mp="2jew6ajix4168">
                Haz clic en cualquier sección de la vista previa para editarla, o
                arrastra los bloques para reordenarlos.
              </p>
              <ul className="space-y-2" data-id="element-963" data-mp="29nibuq5tprjy">
                {blocks.map(block => {
                const definition = blockDefinition(block.type);
                const active = selectedBlockId === block.id;
                return <li key={block.id} draggable={handleId === block.id} onDragStart={e => {
                  setDragId(block.id);
                  e.dataTransfer.effectAllowed = 'move';
                }} onDragEnd={() => {
                  setDragId(null);
                  setDragOverId(null);
                  setHandleId(null);
                }} onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragId && dragId !== block.id) setDragOverId(block.id);
                }} onDragLeave={() => setDragOverId(prev => prev === block.id ? null : prev)} onDrop={() => reorder(block.id)} className={`relative rounded-xl border bg-background shadow-sm transition-colors ${active ? 'border-primary' : 'border-border'} ${dragId === block.id ? 'opacity-50' : ''} ${dragOverId === block.id ? 'before:absolute before:-top-1 before:left-0 before:h-0.5 before:w-full before:rounded-full before:bg-primary' : ''}`} data-id="element-964" data-mp="20p6gybxxc7lp">
                      <div className="flex items-center gap-1 p-2.5" data-id="element-965" data-mp="137wjaarm3duj">
                        <span onPointerDown={() => setHandleId(block.id)} onPointerUp={() => setHandleId(null)} className="flex h-8 w-6 flex-none cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing" aria-hidden="true" data-id="element-966" data-mp="187946pyxaauh">
                          <GripVerticalIcon className="h-4 w-4" data-id="element-967" data-mp="20pd66lcj8vfy" />
                        </span>
                        <button type="button" onClick={() => setSelectedBlockId(active ? null : block.id)} className="min-w-0 flex-1 text-left" data-id="element-968" data-mp="3lcmlvap3gbmf">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-969" data-mp="3v4sb4cnyoexx">
                            {definition.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground" data-id="element-970" data-mp="3q7jsyc2tcawo">
                            {definition.description}
                          </span>
                        </button>
                        <button type="button" aria-label="Guardar en la biblioteca" onClick={() => {
                      setSaveComponentFor(block);
                      setComponentName(definition.label);
                    }} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-971" data-mp="3bgb2k0r36lih">
                          <BookmarkIcon className="h-4 w-4" data-id="element-972" data-mp="mogxz0322n8f" />
                        </button>
                        <button type="button" aria-label={block.visible ? 'Ocultar bloque' : 'Mostrar bloque'} onClick={() => updateBlock({
                      ...block,
                      visible: !block.visible
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-973" data-mp="3fixnq33dl254">
                          {block.visible ? <EyeIcon className="h-4 w-4" data-id="element-974" data-mp="3tj02vhkathgo" /> : <EyeOffIcon className="h-4 w-4" data-id="element-975" data-mp="3ti6vcedimnxh" />}
                        </button>
                        <button type="button" aria-label="Duplicar bloque" onClick={() => duplicateBlock(block)} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-976" data-mp="2wu0u81fsydny">
                          <CopyIcon className="h-4 w-4" data-id="element-977" data-mp="35s31gyq4q2jo" />
                        </button>
                        {definition.removable && <button type="button" aria-label="Eliminar bloque" onClick={() => updateConfig({
                      blocks: blocks.filter(b => b.id !== block.id)
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-978" data-mp="3aknq7lu6hpgc">
                            <Trash2Icon className="h-4 w-4" data-id="element-979" data-mp="2aizhyvooute2" />
                          </button>}
                      </div>

                      {active && <div className="border-t border-border p-4" data-id="element-980" data-mp="25iuycwshhijm">
                          <Tabs defaultValue="contenido" data-id="element-981" data-mp="21i1au4iye5r9">
                            <TabsList data-id="element-982" data-mp="1wkjav3mwz0ve">
                              <TabsTrigger value="contenido" data-id="element-983" data-mp="1wjhm5wcz6fv7">Contenido</TabsTrigger>
                              <TabsTrigger value="estilo" data-id="element-984" data-mp="1rlg2pabn0ej7">Estilo</TabsTrigger>
                            </TabsList>
                            <TabsContent value="contenido" data-id="element-985" data-mp="1mtodb347weun">
                              <BlockSettings block={block} onChange={updateBlock} data-id="element-986" data-mp="1hv3khoraoxg8" />
                            </TabsContent>
                            <TabsContent value="estilo" data-id="element-987" data-mp="1dut6botcn0qa">
                              <StyleSettings block={block} onChange={updateBlock} data-id="element-988" data-mp="18uoz6pebzicq" />
                            </TabsContent>
                          </Tabs>
                        </div>}
                    </li>;
              })}
              </ul>
              <Button className="mt-4 w-full" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-990" data-mp="38wy809pj5eu3" />} onClick={() => setAddOpen(true)} data-id="element-989" data-mp="33ywh52lv4b82">
                Añadir sección
              </Button>
            </TabsContent>

            <TabsContent value="diseno" data-id="element-991" data-mp="2u7ij6z9k7jh1">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-992" data-mp="2z6v6tm898uko">
                <Input id="l-name" label="Nombre interno" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-993" data-mp="2l7dewxznx173" />
                <Input id="l-slug" label="Slug" helperText={`${window.location.origin}/landing/${draft.slug}`} value={draft.slug} onChange={e => update({
                slug: e.target.value
              })} data-id="element-994" data-mp="2paieosscyos1" />
                <Select id="l-form" label="Formulario asociado" placeholder="Sin formulario" options={forms.map(f => ({
                value: f.id,
                label: f.name
              }))} value={draft.formId ?? ''} onChange={e => update({
                formId: e.target.value || undefined
              })} data-id="element-995" data-mp="2g8iu5qi4x2kc" />
                <Select id="l-theme" label="Tema visual" options={THEMES} value={draft.pageConfig.theme} onChange={e => updateConfig({
                theme: e.target.value as LandingTheme
              })} data-id="element-996" data-mp="1tzvcytlpaui0" />
                <Input id="l-color" label="Color principal" type="color" value={draft.pageConfig.primaryColor} onChange={e => updateConfig({
                primaryColor: e.target.value
              })} data-id="element-997" data-mp="23yb58zwgs9t3" />
                <Select id="l-font" label="Tipografía" options={FONT_OPTIONS} value={draft.pageConfig.fontFamily} onChange={e => updateConfig({
                fontFamily: e.target.value
              })} data-id="element-998" data-mp="2cqldwcrpo788" />
                <div data-id="element-999" data-mp="2mnzmjq7e7rpz">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1000" data-mp="2mpkct3mocoi1">
                    Logotipo de la landing
                  </p>
                  <LogoUploader value={draft.pageConfig.logoUrl} onChange={value => updateConfig({
                  logoUrl: value
                })} data-id="element-1001" data-mp="xbfmmvaroh3u" />
                  <p className="mt-2 text-xs text-muted-foreground" data-id="element-1002" data-mp="1hzow7o0ndvzf">
                    Si lo dejas vacío se usa el logotipo de tu empresa.
                  </p>
                </div>
              </div>

              {/* Biblioteca de componentes */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-id="element-1003" data-mp="1r1ocge3u8w1b">
                <div className="mb-3 flex items-center gap-2" data-id="element-1004" data-mp="1vwbxg2zmv8b5">
                  <LayersIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1005" data-mp="20wq35edri660" />
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-1006" data-mp="24ybfmymdwifo">
                    Biblioteca de componentes
                  </h3>
                </div>
                {components.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-1007" data-mp="2epfttw48jz9g">
                    Guarda cualquier bloque con el icono de marcador para reutilizarlo
                    en otras landings.
                  </p> : <ul className="space-y-2" data-id="element-1008" data-mp="2omkgry5wnyrb">
                    {components.map(component => <li key={component.id} className="flex items-center gap-2 rounded-xl border border-border p-3" data-id="element-1009" data-mp="czqo49yhnb57">
                        <span className="min-w-0 flex-1" data-id="element-1010" data-mp="804cg4yb62yi">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1011" data-mp="82qhnxxq86dk">
                            {component.name}
                          </span>
                          <span className="block text-xs text-muted-foreground" data-id="element-1012" data-mp="3u95hu3pc5vtk">
                            {component.category}
                          </span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => addBlock(instantiateComponent(component))} data-id="element-1013" data-mp="qsgbahpr6m3w">
                          Insertar
                        </Button>
                        <button type="button" aria-label="Eliminar componente" onClick={() => deleteComponent(component.id)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-id="element-1014" data-mp="h0alnemz4grv">
                          <Trash2Icon className="h-4 w-4" data-id="element-1015" data-mp="19pr7ftg38zf4" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </TabsContent>

            <TabsContent value="gracias" data-id="element-1016" data-mp="mtog1hlu4pe9">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1017" data-mp="mupqbjv117ux">
                <Input id="t-title" label="Título" value={draft.pageConfig.thankYou.title} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  title: e.target.value
                }
              })} data-id="element-1018" data-mp="93b83f8y1aox" />
                <Input id="t-subtitle" label="Subtítulo" value={draft.pageConfig.thankYou.subtitle ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  subtitle: e.target.value
                }
              })} data-id="element-1019" data-mp="15saiazt38mjr" />
                <Textarea id="t-text" label="Texto" rows={3} value={draft.pageConfig.thankYou.text} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  text: e.target.value
                }
              })} data-id="element-1020" data-mp="3ci77lku03y62" />
                <Switch id="t-video" label="Mostrar vídeo" checked={Boolean(draft.pageConfig.thankYou.showVideo)} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showVideo: checked
                }
              })} data-id="element-1021" data-mp="34z7nte2mo4m6" />
                <Input id="t-videourl" label="URL del vídeo (YouTube, Vimeo, Wistia o mp4)" value={draft.pageConfig.thankYou.videoUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoUrl: e.target.value
                }
              })} data-id="element-1022" data-mp="2q9rze4qkkb9n" />
                <Input id="t-videothumb" label="Miniatura del vídeo (URL)" value={draft.pageConfig.thankYou.videoThumbnailUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoThumbnailUrl: e.target.value
                }
              })} data-id="element-1023" data-mp="2v838uecycjrc" />
                <Input id="t-image" label="Imagen (URL)" value={draft.pageConfig.thankYou.imageUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  imageUrl: e.target.value
                }
              })} data-id="element-1024" data-mp="z0keln0w91vq" />
                <Switch id="t-cal" label="Mostrar botón de agendar llamada" checked={draft.pageConfig.thankYou.showCalendly} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showCalendly: checked
                }
              })} data-id="element-1025" data-mp="13y2r1ne4dz8h" />
                <Input id="t-calurl" label="URL de Calendly" value={draft.pageConfig.thankYou.calendlyUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  calendlyUrl: e.target.value
                }
              })} data-id="element-1026" data-mp="1rmvojoae4f57" />
                <Switch id="t-wa" label="Mostrar botón de WhatsApp" checked={draft.pageConfig.thankYou.showWhatsapp} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showWhatsapp: checked
                }
              })} data-id="element-1027" data-mp="3ojl308xsnik1" />
                <Input id="t-wanum" label="Número de WhatsApp" value={draft.pageConfig.thankYou.whatsappNumber ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  whatsappNumber: e.target.value
                }
              })} data-id="element-1028" data-mp="1u7jw5dnzxm3p" />
                <Switch id="t-home" label="Mostrar botón de volver al inicio" checked={draft.pageConfig.thankYou.showHome} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showHome: checked
                }
              })} data-id="element-1029" data-mp="1bas7eopoqes9" />
                <Input id="t-redirect" label="Redirección automática (URL)" value={draft.pageConfig.thankYou.autoRedirectUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  autoRedirectUrl: e.target.value
                }
              })} data-id="element-1030" data-mp="3ao638n5tlp8n" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Vista previa editable, fija mientras el panel izquierdo se desplaza */}
        <div className="h-[860px] min-w-0 xl:sticky xl:top-4" data-id="element-1031" data-mp="3rwyupju7tdri">
          <DevicePreview device={device} onDeviceChange={setDevice} toolbar={tab === 'gracias' ? <span className="text-xs text-muted-foreground" data-id="element-1033" data-mp="3mz6s9vvy2992">
                  Vista previa de la página de gracias
                </span> : selectedBlock ? <span className="text-xs text-muted-foreground" data-id="element-1034" data-mp="3e5k1ylai9y79">
                  Editando: {blockDefinition(selectedBlock.type).label}
                </span> : <span className="text-xs text-muted-foreground" data-id="element-1035" data-mp="eivjswlzh5pw">
                  Haz clic en una sección para editarla
                </span>} data-id="element-1032" data-mp="3rw6zwb8fzk2a">
            {tab === 'gracias' ? <ThankYouView config={draft.pageConfig.thankYou} logoUrl={draft.pageConfig.logoUrl ?? organization?.brand.logoUrl ?? organization?.logoUrl} primaryColor={draft.pageConfig.primaryColor} preview data-id="element-1036" data-mp="1sede5o77c6v9" /> : <LandingRenderer landing={draft} form={form} preview selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} data-id="element-1037" data-mp="21f1s6iwli785" />}
          </DevicePreview>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir sección" data-id="element-1038" data-mp="2bczeh0uw9ty4">
        {components.length > 0 && <div className="mb-6" data-id="element-1039" data-mp="2gbsv325ha12i">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1040" data-mp="2gb0zhrbvs3c8">
              Mis componentes
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1041" data-mp="2l5580f0sojff">
              {components.map(component => <li key={component.id} data-id="element-1042" data-mp="2p6gwgh7xmym1">
                  <button type="button" onClick={() => addBlock(instantiateComponent(component))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1043" data-mp="2u3z7cd5ifnwo">
                    <span className="block text-sm font-medium text-foreground" data-id="element-1044" data-mp="2les1y4tlv98b">
                      {component.name}
                    </span>
                    <span className="block text-xs text-muted-foreground" data-id="element-1045" data-mp="2gew46nbi4e17">
                      {component.category}
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1046" data-mp="22gpj67rni8iy">
          Secciones disponibles
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1047" data-mp="1xjfifvytaaft">
          {BLOCK_LIBRARY.map(definition => <li key={definition.type} data-id="element-1048" data-mp="2cg6hg44kpk4w">
              <button type="button" onClick={() => addBlock(newBlock(definition.type))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1049" data-mp="2cd2bb17jsebl">
                <span className="block text-sm font-medium text-foreground" data-id="element-1050" data-mp="3iv7igo8ggsic">
                  {definition.label}
                </span>
                <span className="block text-xs text-muted-foreground" data-id="element-1051" data-mp="3qegp49pkud3p">
                  {definition.description}
                </span>
              </button>
            </li>)}
        </ul>
      </Modal>

      <Modal open={Boolean(saveComponentFor)} onClose={() => setSaveComponentFor(null)} title="Guardar en la biblioteca" footer={<>
            <Button variant="ghost" onClick={() => setSaveComponentFor(null)} data-id="element-1053" data-mp="3lfx1m4vc4t98">
              Cancelar
            </Button>
            <Button onClick={confirmSaveComponent} data-id="element-1054" data-mp="hyx23sfi1qx3">Guardar componente</Button>
          </>} data-id="element-1052" data-mp="3hf3gtkd6vukk">
        <Input id="component-name" label="Nombre del componente" value={componentName} onChange={e => setComponentName(e.target.value)} data-id="element-1055" data-mp="i0re2h8sm8p1" />
        <p className="mt-3 text-sm text-muted-foreground" data-id="element-1056" data-mp="88bvpitl3i3m">
          Se guardará con su contenido y su estilo actual para que puedas reutilizarlo
          en cualquier otra landing de tu empresa.
        </p>
      </Modal>
    </>;
}